import { promises as fs } from 'fs';
import path from 'path';
import glob from 'fast-glob';
import matter from 'gray-matter';
import { execSync } from 'child_process';
import Parser from 'rss-parser';
import type { ParsedChangelog, ExampleGroup, WebflowChangelogEntry } from './types.js';

const DOCS_OUTPUT_DIR = '.docs/organized';
const DOCS_DIR = path.join(DOCS_OUTPUT_DIR, 'docs');
const EXAMPLES_DIR = path.join(DOCS_OUTPUT_DIR, 'examples');
const CHANGELOGS_DIR = path.join(DOCS_OUTPUT_DIR, 'changelogs');

// WorkOS monorepo configuration
const WORKOS_REPO_URL = process.env.GITHUB_TOKEN
  ? `https://${process.env.GITHUB_TOKEN}@github.com/workos/workos.git`
  : 'git@github.com:workos/workos.git';
const TEMP_CLONE_DIR = '.tmp-workos-clone';

async function ensureDir(dir: string): Promise<void> {
  try {
    await fs.mkdir(dir, { recursive: true });
  } catch (error) {
    if ((error as NodeJS.ErrnoException).code !== 'EEXIST') {
      throw error;
    }
  }
}

async function cloneWorkOSRepo(): Promise<string> {
  console.log('🔄 Cloning WorkOS monorepo for latest docs...');

  // Clean up any existing temp directory
  try {
    await fs.rm(TEMP_CLONE_DIR, { recursive: true, force: true });
  } catch {
    // Directory might not exist, which is fine
  }

  try {
    // Shallow clone to get just the latest content
    execSync(`git clone --depth 1 ${WORKOS_REPO_URL} ${TEMP_CLONE_DIR}`, {
      stdio: 'inherit',
    });

    console.log('✅ Successfully cloned WorkOS monorepo');
    return TEMP_CLONE_DIR;
  } catch (error) {
    console.error('❌ Failed to clone WorkOS repo. Make sure you have access to the repository.');
    throw error;
  }
}

async function cleanupTempDir(): Promise<void> {
  try {
    await fs.rm(TEMP_CLONE_DIR, { recursive: true, force: true });
    console.log('🧹 Cleaned up temporary clone directory');
  } catch (error) {
    console.warn('⚠️ Could not clean up temporary directory:', error);
  }
}

async function copyDocs(sourceRoot: string = '.'): Promise<void> {
  console.log('📚 Organizing documentation...');

  await ensureDir(DOCS_DIR);

  // Look for docs in the appropriate location
  const useLocal = process.env.USE_LOCAL_DOCS === 'true';
  let docsPaths: string[];

  if (useLocal) {
    // Look for docs/ directory in the current workspace or parent directories (development)
    docsPaths = await glob(['docs/**/*.{md,mdx}', '../docs/**/*.{md,mdx}'], {
      ignore: ['**/node_modules/**'],
    });
  } else {
    // Look for docs in the cloned repo's packages/docs/content directory (default)
    docsPaths = await glob([`${sourceRoot}/packages/docs/content/**/*.{md,mdx}`], {
      ignore: ['**/node_modules/**'],
    });
  }

  for (const docPath of docsPaths) {
    const content = await fs.readFile(docPath, 'utf-8');
    const parsed = matter(content);

    // Normalize the path
    let relativePath: string;
    if (useLocal) {
      relativePath = docPath.replace(/^\.\.\//, '').replace(/^docs\//, '');
    } else {
      // Remove the temp clone path and packages/docs/content prefix
      relativePath = docPath.replace(`${sourceRoot}/packages/docs/content/`, '').replace(/^\.\.\//, '');
    }

    const outputPath = path.join(DOCS_DIR, relativePath);

    await ensureDir(path.dirname(outputPath));

    // Ensure parsed.data is an object and add frontmatter with original path if not present
    if (typeof parsed.data !== 'object' || parsed.data === null) {
      parsed.data = {};
    }
    if (!parsed.data.originalPath) {
      parsed.data.originalPath = docPath;
    }

    const updatedContent = matter.stringify(parsed.content, parsed.data);
    await fs.writeFile(outputPath, updatedContent);
  }

  console.log(`✅ Copied ${docsPaths.length} documentation files`);
}

async function organizeExamples(sourceRoot: string = '.'): Promise<void> {
  console.log('💻 Organizing code examples...');

  await ensureDir(EXAMPLES_DIR);

  // Look for examples in the appropriate location
  const useLocal = process.env.USE_LOCAL_DOCS === 'true';
  let examplePaths: string[];

  if (useLocal) {
    // Look for examples/ directory (development)
    examplePaths = await glob(['examples/**/*.{ts,js,tsx,jsx}', '../examples/**/*.{ts,js,tsx,jsx}'], {
      ignore: ['**/node_modules/**', '**/dist/**'],
    });
  } else {
    // Look for examples in the cloned repo (default)
    examplePaths = await glob([`${sourceRoot}/examples/**/*.{ts,js,tsx,jsx}`], {
      ignore: ['**/node_modules/**', '**/dist/**'],
    });
  }

  const exampleGroups: Map<string, string[]> = new Map();

  for (const examplePath of examplePaths) {
    const content = await fs.readFile(examplePath, 'utf-8');

    // Group examples by their directory
    let pathParts: string[];
    if (useLocal) {
      pathParts = examplePath
        .replace(/^\.\.\//, '')
        .replace(/^examples\//, '')
        .split('/');
    } else {
      pathParts = examplePath.replace(`${sourceRoot}/examples/`, '').split('/');
    }

    const groupName = pathParts[0] || 'misc';

    if (!exampleGroups.has(groupName)) {
      exampleGroups.set(groupName, []);
    }

    const relativePath = pathParts.join('/');
    const outputPath = path.join(EXAMPLES_DIR, relativePath);

    await ensureDir(path.dirname(outputPath));
    await fs.writeFile(outputPath, content);

    exampleGroups.get(groupName)!.push(relativePath);
  }

  // Create an index file for each group
  for (const [groupName, files] of exampleGroups) {
    const indexPath = path.join(EXAMPLES_DIR, groupName, 'index.json');
    const groupData: ExampleGroup = {
      name: groupName,
      path: groupName,
      files,
    };
    await fs.writeFile(indexPath, JSON.stringify(groupData, null, 2));
  }

  console.log(`✅ Organized ${examplePaths.length} examples into ${exampleGroups.size} groups`);
}

async function parseChangelogs(sourceRoot: string = '.'): Promise<void> {
  console.log('📝 Parsing changelogs...');

  await ensureDir(CHANGELOGS_DIR);

  // First, fetch the RSS changelog from WorkOS (unless using local docs)
  if (process.env.USE_LOCAL_DOCS !== 'true') {
    const rssEntries = await parseRSSChangelog();
    if (rssEntries.length > 0) {
      const truncatedRSSEntries = truncateChangelog(rssEntries, 25); // Keep more RSS entries since they're the main changelog
      const rssOutputPath = path.join(CHANGELOGS_DIR, 'workos-platform.json');
      await fs.writeFile(rssOutputPath, JSON.stringify(truncatedRSSEntries, null, 2));
    }
  }

  // Look for CHANGELOG.md files in packages
  const useLocal = process.env.USE_LOCAL_DOCS === 'true';
  let changelogPaths: string[];

  if (useLocal) {
    changelogPaths = await glob(['packages/*/CHANGELOG.md', '../packages/*/CHANGELOG.md'], {
      ignore: ['**/node_modules/**'],
    });
  } else {
    changelogPaths = await glob([`${sourceRoot}/packages/*/CHANGELOG.md`], {
      ignore: ['**/node_modules/**'],
    });
  }

  for (const changelogPath of changelogPaths) {
    const content = await fs.readFile(changelogPath, 'utf-8');
    const packageName = path.basename(path.dirname(changelogPath));

    // Parse changelog and truncate to recent entries
    const parsedChangelog = parseChangelogContent(content, packageName);
    // Mark as markdown source
    parsedChangelog.forEach((entry) => {
      entry.source = 'markdown';
    });
    const truncatedChangelog = truncateChangelog(parsedChangelog, 10); // Keep last 10 entries

    const outputPath = path.join(CHANGELOGS_DIR, `${packageName}.json`);
    await fs.writeFile(outputPath, JSON.stringify(truncatedChangelog, null, 2));
  }

  console.log(`✅ Parsed ${changelogPaths.length} markdown changelogs and fetched RSS changelog`);
}

function parseChangelogContent(content: string, packageName: string): ParsedChangelog[] {
  const entries: ParsedChangelog[] = [];
  const lines = content.split('\n');

  let currentEntry: Partial<ParsedChangelog> | null = null;

  for (const line of lines) {
    // Match version headers like ## [1.0.0] - 2023-01-01
    const versionMatch = line.match(/^##\s*\[?([^\]]+)\]?\s*-\s*(.+)/);
    if (versionMatch) {
      if (currentEntry) {
        entries.push(currentEntry as ParsedChangelog);
      }
      currentEntry = {
        package: packageName,
        version: versionMatch[1],
        date: versionMatch[2],
        changes: [],
      };
      continue;
    }

    // Match change items like - Added new feature
    const changeMatch = line.match(/^[-*]\s*(.+)/);
    if (changeMatch && currentEntry) {
      currentEntry.changes = currentEntry.changes || [];
      currentEntry.changes.push(changeMatch[1]);
    }
  }

  if (currentEntry) {
    entries.push(currentEntry as ParsedChangelog);
  }

  return entries;
}

function truncateChangelog(entries: ParsedChangelog[], maxEntries: number): ParsedChangelog[] {
  return entries.slice(0, maxEntries);
}

async function parseRSSChangelog(): Promise<ParsedChangelog[]> {
  console.log('🌐 Fetching WorkOS changelog from RSS feed...');

  const parser = new Parser({
    customFields: {
      item: [
        ['guid', 'guid'],
        ['description', 'description'],
      ],
    },
  });

  try {
    const feed = await parser.parseURL('https://workos.com/changelog/rss.xml');
    const entries: ParsedChangelog[] = [];

    for (const item of feed.items) {
      // Extract title and date
      const title = item.title || 'Untitled';
      const pubDate = item.pubDate || '';
      const link = item.link || '';
      const guid = item.guid || '';
      const description = item.description || '';

      // Parse the date
      let formattedDate = pubDate;
      if (pubDate) {
        try {
          const date = new Date(pubDate);
          formattedDate = date.toISOString().split('T')[0]; // YYYY-MM-DD format
        } catch {
          // Keep original date string if parsing fails
        }
      }

      // Clean up description to extract meaningful content
      const cleanDescription = description
        .replace(/<[^>]*>/g, '') // Remove HTML tags
        .replace(/\s+/g, ' ') // Normalize whitespace
        .trim();

      const entry: ParsedChangelog = {
        package: 'workos-platform',
        version: title,
        date: formattedDate,
        changes: cleanDescription ? [cleanDescription] : [title],
        source: 'rss',
        url: link,
        guid: guid,
      };

      entries.push(entry);
    }

    console.log(`✅ Fetched ${entries.length} changelog entries from RSS feed`);
    return entries;
  } catch (error) {
    console.error('❌ Failed to fetch RSS changelog:', error);
    // Return empty array instead of throwing to allow fallback to local changelogs
    return [];
  }
}

async function main(): Promise<void> {
  let sourceRoot = '.';

  try {
    console.log('🚀 Starting WorkOS docs preparation...');

    // Clean existing organized docs
    try {
      await fs.rm(DOCS_OUTPUT_DIR, { recursive: true, force: true });
    } catch {
      // Directory might not exist, which is fine
    }

    await ensureDir(DOCS_OUTPUT_DIR);

    // Clone WorkOS repo unless using local docs
    if (process.env.USE_LOCAL_DOCS !== 'true') {
      sourceRoot = await cloneWorkOSRepo();
    }

    // Run all preparation steps
    await Promise.all([copyDocs(sourceRoot), organizeExamples(sourceRoot), parseChangelogs(sourceRoot)]);

    console.log('✨ WorkOS docs preparation complete!');
  } catch (error) {
    console.error('❌ Error preparing docs:', error);
    process.exit(1);
  } finally {
    // Clean up temp directory if we cloned the repo
    if (process.env.USE_LOCAL_DOCS !== 'true') {
      await cleanupTempDir();
    }
  }
}

// Run the script if called directly
if (process.env.PREPARE === 'true') {
  main();
}
